import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { createKnowledgeArticle } from "store/actions/chatbotKnowledgeAction";
import { CATEGORIES } from "views/ChatbotKnowledge/KnowledgeArticleForm";

const AddKnowledgeArticleModal = ({ modal, toggle, onSuccess }) => {
  const dispatch = useDispatch();
  const { saveArticleLoader } = useSelector((state) => state.chatbotKnowledge);

  const initialFormState = {
    title: "",
    category: "faq",
    status: "published",
    keywords: [],
    content: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    if (modal) {
      setFormData(initialFormState);
      setKeywordInput("");
    }
  }, [modal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) return;

    const tagsToAdd = trimmed
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t && !formData.keywords.includes(t));

    if (tagsToAdd.length > 0) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...prev.keywords, ...tagsToAdd],
      }));
    }
    setKeywordInput("");
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddKeyword();
    } else if (
      e.key === "Backspace" &&
      !keywordInput &&
      formData.keywords.length > 0
    ) {
      handleRemoveKeyword(formData.keywords.length - 1);
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter Article Title");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a Category");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Please enter Article Content");
      return;
    }

    let finalKeywords = [...formData.keywords];
    if (keywordInput.trim()) {
      const extra = keywordInput
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t && !finalKeywords.includes(t));
      finalKeywords = [...finalKeywords, ...extra];
    }

    const payload = {
      title: formData.title.trim(),
      category: formData.category,
      status: formData.status,
      keywords: finalKeywords,
      content: formData.content.trim(),
    };

    dispatch(
      createKnowledgeArticle(payload, () => {
        toggle();
        if (onSuccess) onSuccess();
      })
    );
  };

  return (
    <Modal isOpen={modal} toggle={toggle} size="lg" centered scrollable={true}>
      <ModalHeader toggle={toggle} className="border-bottom">
        Add Knowledge Article
      </ModalHeader>

      <ModalBody className="py-3">
        <Row className="d-flex">
          {/* Article Title - Full Width */}
          <Col xs="12" className="my-2">
            <Label className="mb-0 my-auto">
              Article Title <span className="text-danger">*</span>
            </Label>
            <Input
              name="title"
              placeholder="e.g. When do service providers get paid?"
              value={formData.title}
              required
              onChange={handleChange}
            />
          </Col>

          {/* Category Dropdown & Status Dropdown in One Line */}
          <Col xs="12" sm="6" className="my-2">
            <Label className="mb-0 my-auto">
              Category <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </Input>
          </Col>

          <Col xs="12" sm="6" className="my-2">
            <Label className="mb-0 my-auto">
              Status <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </Input>
          </Col>

          {/* Keywords - Modern Tag Input */}
          <Col xs="12" className="my-2">
            <Label className="mb-0 my-auto">Keywords (Tags)</Label>
            <div
              className="form-control d-flex flex-wrap align-items-center mt-1"
              style={{
                minHeight: "46px",
                height: "auto",
                padding: "4px 8px",
                cursor: "text",
                backgroundColor: "#fff",
              }}
              onClick={() => {
                const el = document.getElementById("add-modal-keyword-input");
                if (el) el.focus();
              }}
            >
              {formData.keywords && formData.keywords.length > 0 &&
                formData.keywords.map((kw, idx) => (
                  <span
                    key={idx}
                    className="badge badge-pill mr-2 my-1 px-3 py-2 d-inline-flex align-items-center"
                    style={{
                      backgroundColor: "#e8f5f4",
                      color: "#008080",
                      fontSize: "13px",
                      fontWeight: "500",
                      border: "1px solid #b2dfdb",
                    }}
                  >
                    {kw}
                    <i
                      className="fas fa-times ml-2 text-danger"
                      style={{ cursor: "pointer", fontSize: "11px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveKeyword(idx);
                      }}
                      title="Remove tag"
                    />
                  </span>
                ))}
              <input
                id="add-modal-keyword-input"
                type="text"
                placeholder={
                  formData.keywords.length === 0
                    ? "Type keyword and press Enter or comma..."
                    : "Add more..."
                }
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                onBlur={handleAddKeyword}
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  flex: "1 1 180px",
                  fontSize: "14px",
                  background: "transparent",
                  padding: "4px 6px",
                  minWidth: "160px",
                }}
              />
            </div>
            <small className="text-muted">
              Type keyword and press Enter or comma to add.
            </small>
          </Col>

          {/* Article Content */}
          <Col md="12" className="my-2">
            <Label className="mb-0 my-auto">
              Article Content <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              name="content"
              rows="7"
              placeholder="Payment is released to the provider 3 business days after the buyer approves the work..."
              value={formData.content}
              required
              onChange={handleChange}
              style={{ fontSize: "14px", lineHeight: "1.6" }}
            />
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-top">
        <Button
          color="secondary"
          onClick={toggle}
          disabled={saveArticleLoader}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={saveArticleLoader}
        >
          {saveArticleLoader ? <Spinner size="sm" /> : "Save Article"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddKnowledgeArticleModal;
